/**
 * SearchSpring API client for fetching search suggestions and products.
 */

import { trackingParams } from '@theme/searchspring-beacon';

/**
 * Fetch search suggestions from SearchSpring Suggest API.
 * @param {string} siteId - The SearchSpring site ID
 * @param {string} searchTerm - The term to search for
 * @param {object} options - Configuration options
 * @param {string} [options.suggestionsQty='4'] - Number of suggestions to return
 * @param {string} [options.productsQty='10'] - Number of products for suggestions
 * @param {AbortSignal} [options.signal] - Abort signal for request cancellation
 * @returns {Promise<string[]>} Array of search suggestions
 */
export async function getSearchSuggestions(siteId, searchTerm, options = {}) {
  const { suggestionsQty = '4', productsQty = '10', signal } = options;

  if (!siteId) return [];

  const url = new URL(`https://${siteId}.a.searchspring.io/api/suggest/query`);
  url.searchParams.set('q', searchTerm);
  url.searchParams.set('language', 'en');
  url.searchParams.set('suggestionCount', suggestionsQty);
  url.searchParams.set('productCount', productsQty);
  url.searchParams.set('siteId', siteId);

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { accept: 'application/json' },
      signal,
    });

    if (signal?.aborted) return [];

    if (!response.ok) {
      throw new Error(`SearchSpring API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (signal?.aborted) return [];

    const suggestions = [];

    if (data.suggested?.text) {
      suggestions.push(data.suggested.text);
    }

    if (Array.isArray(data.alternatives)) {
      /** @type {Array<{text?: string}>} */
      const alternatives = data.alternatives;
      alternatives.forEach((item) => {
        if (item?.text) {
          suggestions.push(item.text);
        }
      });
    }

    return suggestions;
  } catch (error) {
    if (signal?.aborted) return [];
    console.error('SearchSpring fetch error:', error);
    return [];
  }
}

/**
 * Fetch search products from SearchSpring API.
 * @param {string} siteId - The SearchSpring site ID
 * @param {string} searchTerm - The term to search for
 * @param {object} options - Configuration options
 * @param {string} [options.productsQty='10'] - Number of products to return
 * @param {AbortSignal} [options.signal] - Abort signal for request cancellation
 * @returns {Promise<{results: Array<{url?: string, imageUrl?: string, title?: string, name?: string, price?: string, sale_price?: string}>, didYouMean: {query: string} | null, responseId: string}>} Results, optional didYouMean suggestion, and the responseId beacon events correlate on
 */
export async function getSearchProducts(siteId, searchTerm, options = {}) {
  const { productsQty = '10', signal } = options;

  if (!siteId) return { results: [], didYouMean: null, responseId: '' };

  const url = new URL(`https://${siteId}.a.searchspring.io/api/search/autocomplete.json`);
  url.searchParams.set('q', searchTerm);
  url.searchParams.set('resultsFormat', 'json');
  url.searchParams.set('resultsPerPage', productsQty);
  url.searchParams.set('siteId', siteId);
  // Required for Beacon 2.0 — enables the API's tracking event generation.
  url.searchParams.set('beacon', 'true');
  // Required for reporting and personalization; must match the beacon's IDs.
  for (const [key, value] of Object.entries(trackingParams())) {
    url.searchParams.set(key, value);
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { accept: 'application/json' },
      signal,
    });

    if (signal?.aborted) return { results: [], didYouMean: null, responseId: '' };

    if (!response.ok) {
      throw new Error(`SearchSpring API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (signal?.aborted) return { results: [], didYouMean: null, responseId: '' };

    const didYouMean = data.didYouMean?.query ? { query: data.didYouMean.query } : null;

    return {
      results: data.results || [],
      didYouMean,
      // Beacon events correlate on this; see assets/searchspring-beacon.js.
      responseId: data.responseId || '',
    };
  } catch (error) {
    if (signal?.aborted) return { results: [], didYouMean: null, responseId: '' };
    console.error('SearchSpring fetch error:', error);
    return { results: [], didYouMean: null, responseId: '' };
  }
}

